// Format helpers shared across modules. Pulled out of my-accounts.tsx so
// other modules can render addresses and currency the same way without
// duplicating the regex / Intl logic.

/**
 * Compress a single-line address to a "City, ST" summary.
 * Falls back to a 32-char truncation when no state token is found.
 *
 * Examples:
 *   "1234 Main St, Denver, CO 80205"     → "Denver, CO"
 *   "Suite 200, Austin TX"               → "Suite 200, Austin TX" (no match)
 *   "Long single-line address with..."   → "Long single-line address with…"
 */
export function shortAddress(addr: string | null | undefined): string {
  if (!addr) return '';
  const m = addr.match(/([A-Za-z .'-]+),\s*([A-Z]{2})(?:\s*\d{5}(?:-\d{4})?)?/);
  if (m) return `${m[1].trim()}, ${m[2]}`;
  return addr.length > 32 ? addr.slice(0, 30) + '…' : addr;
}

/**
 * Format a number as US currency. Returns empty string for null/undefined/NaN
 * so callers can short-circuit rendering with `&&`.
 */
export function fmtCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Format a BARE Postgres DATE ("2026-08-25") as "Mon D, YYYY".
 *
 * ⛔ TCSD-0070: `new Date('2026-08-25')` parses as UTC midnight and then renders
 * in local time, so every bare date west of Greenwich comes out A DAY EARLY.
 * `fmtDate` below has that hazard and is correct only for timestamptz. This
 * anchors to LOCAL midnight instead.
 *
 * The same three-line fix had been written out by hand in four separate files
 * before this existed. One vocabulary, written once — the same reason
 * shared/coi-state.ts exists.
 */
export function fmtBareDate(d: string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(`${d.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Today as YYYY-MM-DD on the person's own device (their local calendar day).
 * ⛔ Never `new Date().toISOString().slice(0, 10)` for a date a person picks:
 * that is the UTC day, which is already tomorrow after ~7 pm Central (item 944,
 * the feedback date).
 */
export function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * Format an ISO date string as "Mon D, YYYY". Returns empty string for
 * null / undefined / unparseable inputs.
 *
 * ⛔ For a timestamptz. For a bare DATE column use fmtBareDate — see TCSD-0070.
 */
export function fmtDate(d: string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
